import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useContext } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { RelativeTime } from "@design-system/RelativeTime/RelativeTime";
import { formatDateOnly } from "@helpers/date";
import dateCellStyles from "@components/invoices/views/invoicesView/cells/DateCell.module.scss";
import entityCellStyles from "@components/invoices/views/invoicesView/cells/EntityCell.module.scss";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { useEntitiesById } from "@hooks/useEntities";
import { AccrualImporterContext } from "./accrualImporter.context";
import { resolveAccrualRowExtraction } from "./accrualImporter.helpers";
import { AccrualImporterViewRow } from "./accrualImporter.types";

type PageCellProps = CellContext<
    ViewTableFeatures,
    AccrualImporterViewRow,
    unknown
>;

export const AccrualImporterInvoiceDateCell = memo(({ row }: PageCellProps) => {
    const { mode } = useContext(AccrualImporterContext);
    const financialDocument = row.original.financialDocument;
    const documentDate = financialDocument
        ? resolveAccrualRowExtraction(
              financialDocument,
              mode,
              row.original.extractionId,
          )?.documentDate
        : null;

    return (
        <ViewTableCell className={dateCellStyles.dateCell}>
            {formatDateOnly(documentDate)}
        </ViewTableCell>
    );
});

export const AccrualImporterDueDateCell = memo(({ row }: PageCellProps) => {
    const { mode } = useContext(AccrualImporterContext);
    const financialDocument = row.original.financialDocument;
    const dueDate = financialDocument
        ? resolveAccrualRowExtraction(
              financialDocument,
              mode,
              row.original.extractionId,
          )?.dueDate
        : null;

    return (
        <ViewTableCell className={dateCellStyles.dateCell}>
            {formatDateOnly(dueDate)}
        </ViewTableCell>
    );
});

export const AccrualImporterEntityBadgeCell = memo(({ row }: PageCellProps) => {
    const entitiesById = useEntitiesById();
    const entityId = row.original.financialDocument?.entityId;
    const entity = entityId != null ? entitiesById.get(entityId) : undefined;

    return (
        <ViewTableCell className={entityCellStyles.entityCell}>
            {entity && (
                <AvatarTooltip content={entity.name}>
                    <EntityAvatar
                        size="xs"
                        entity={entity}
                        copyId={entity.id}
                    />
                </AvatarTooltip>
            )}
        </ViewTableCell>
    );
});

export const AccrualImporterDateImportedCell = memo(
    ({ row }: PageCellProps) => {
        const createdAt = row.original.financialDocument?.createdAt;

        return (
            <ViewTableCell className={dateCellStyles.dateCell}>
                {createdAt && <RelativeTime date={new Date(createdAt)} />}
            </ViewTableCell>
        );
    },
);
